import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useAuthContext } from "../../context/AuthContext";
import ActiveCampaign from "../../services/activeCampaign";
import apiMagicLink from "../../services/apiMagicLink";
import Alert from "../../components/Alert";
import Button from "../../components/Button";
import AnimationContainer from "../../components/AnimationContainer";
import PasswordInput from "../../components/PasswordInput";
import LoginContainer from "../../components/LoginContainer";
import LoginPageTitle from "../../components/LoginPageTitle";

import * as S from "./styles";

const schema = yup.object({
  password: yup
    .string()
    .required("Por favor, preencha a senha")
    // eslint-disable-next-line no-template-curly-in-string
    .min(6, "A senha deve conter no mínimo ${min} caracteres")
    .trim(),
});

const LoginPassword = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const { signIn, setIsLoggedIn } = useAuthContext();
  const history = useHistory();

  const [alert, setAlert] = useState({});
  const [loading, setLoading] = useState({
    password: false,
    forgotPassword: false,
  });

  const location = useLocation();
  const props = location.state;

  useEffect(() => {
    if (!props || !props.email) {
      history.push("/explore/entrar");
    }
  }, [props]);

  const onSubmit = async (data) => {
    setLoading({ ...loading, password: true });

    try {
      const response = await signIn(props.email, data.password);

      if (response?.challengeName === "NEW_PASSWORD_REQUIRED") {
        history.push("/nova-senha", {
          email: props.email,
          oldPassword: data.password,
        });
      } else {
        setIsLoggedIn(true);
        ActiveCampaign.onSuccessLogin(props.email);

        if (props.changePassword) {
          return history.replace("/alterar-senha");
        }

        history.replace("/explore");
      }
    } catch (error) {
      let message =
        "Houve um problema, por favor, verifique os dados e tente novamente.";

      if (error && error.message === "Incorrect username or password.") {
        message = (
          <>
            Senha incorreta. Verifique e tente novamente. Se tiver
            dificuldades,&nbsp;
            <S.ForgotPasswordAlertLink onClick={handleForgotPassword}>
              recupere a sua senha.
            </S.ForgotPasswordAlertLink>
          </>
        );
      }

      ActiveCampaign.onFailedLogin(props.email);

      setAlert({
        message,
        type: "error",
      });
    } finally {
      setLoading({ ...loading, password: false });
    }
  };

  async function handleForgotPassword() {
    setLoading({ ...loading, forgotPassword: true });
    await apiMagicLink.post("/signin", {
      email: props.email,
      pass: true,
      appExplore: true,
    });
    history.push("/explore/entrar/info", {
      email: props.email,
      type: "password",
    });
  }

  return (
    <LoginContainer>
      <S.LoginWrapper>
        <AnimationContainer show={!!alert.message}>
          <Alert variant={alert.type} text={alert.message} />
        </AnimationContainer>

        <LoginPageTitle
          title={"Entrar na conta"}
          subtitle={
            <>
              Digite a sua <b> senha </b> para acessar.
            </>
          }
        />

        <S.Form onSubmit={handleSubmit(onSubmit)}>
          <PasswordInput
            label={"Senha"}
            inputName={"password"}
            registerFunction={register}
            errors={errors}
          />

          <Button
            variant={"white"}
            size={"full"}
            type="submit"
            disabled={loading.password}
            loading={loading.password}
            data-testid={"login-button"}
          >
            Entrar
          </Button>
        </S.Form>
        <S.ForgotPasswordButton
          type="button"
          onClick={handleForgotPassword}
          disabled={loading.forgotPassword}
        >
          Esqueceu?&nbsp;
          <span>Recupere a sua senha</span>
          {loading.forgotPassword && (
            <>
              <div className="spinner black" />
            </>
          )}
        </S.ForgotPasswordButton>
      </S.LoginWrapper>
    </LoginContainer>
  );
};

export default LoginPassword;
